import React, { useEffect, useState, memo, useCallback } from "react";
import axios from "axios";
import { getToken } from "../../utils/auth";
import { useTranslation } from "react-i18next";
import CONFIG from "../../config";

const ReviewsAdmin = memo(() => {
  const { t, i18n } = useTranslation();
  const [reviews, setReviews] = useState([]);
  const [editing, setEditing] = useState(null);
  const [editData, setEditData] = useState({ rating: 5, comment: "" });
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(new Set());

  const token = getToken();

  const isArabic = i18n.language?.startsWith('ar');



  const getHeaders = useCallback(() => {
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, [token]);

  const fetchReviews = useCallback(async (p = 1) => {
    try {
      const res = await axios.get(`${CONFIG.API_URL}/api/admin/reviews?page=${p}&q=${encodeURIComponent(q)}`, {
        headers: getHeaders()
      });
      setReviews(res.data.data || []);
      setTotal(res.data.total || res.data.meta?.total || 0);
      setPage(p);
    } catch (err) {
      console.error(err);
      const msg = err.response?.data?.message || err.message || "Failed to fetch reviews";
      alert(msg);
    }
  }, [q, getHeaders]);

  useEffect(() => { fetchReviews(); }, [fetchReviews]);
  useEffect(() => { fetchReviews(1); }, [q, fetchReviews]);

  const toggle = (id) => {
    const s = new Set(selected);
    s.has(id) ? s.delete(id) : s.add(id);
    setSelected(s);
  };

  const approve = async () => {
    try {
      await axios.post(
        `${CONFIG.API_URL}/api/admin/reviews/approve`,
        { ids: Array.from(selected) },
        { headers: getHeaders() }
      );
      setSelected(new Set());
      fetchReviews();
    } catch (err) {
      console.error(err);
      alert("Approve failed");
    }
  };

  const del = async () => {
    try {
      await axios.post(
        `${CONFIG.API_URL}/api/admin/reviews/delete`,
        { ids: Array.from(selected) },
        { headers: getHeaders() }
      );
      setSelected(new Set());
      fetchReviews();
    } catch (err) {
      console.error(err);
      alert("Delete failed");
    }
  };

  const verify = async (status) => {
    try {
      await axios.post(
        `${CONFIG.API_URL}/api/admin/reviews/verify`,
        { ids: Array.from(selected), is_verified: status },
        { headers: getHeaders() }
      );
      setSelected(new Set());
      fetchReviews();
    } catch (err) {
      console.error(err);
      alert("Verification failed");
    }
  };

  return (
    <div className="max-w-7xl mx-auto my-12 p-8 bg-white rounded-[40px] shadow-2xl border border-gray-100 mt-24">
      <div className="flex justify-between items-center mb-12">
        <h2 className="text-4xl font-black text-gray-900 tracking-tight">{t('admin.reviews_page.title')}</h2>
        <div className="flex items-center gap-4">
          <button
            onClick={() => window.history.back()}
            className="flex items-center gap-2 text-gray-500 hover:text-brand-primary transition-all text-sm font-medium group"
          >
            <span className={`inline-block transition-transform group-hover:-translate-x-1 ${isArabic ? "rotate-180 group-hover:translate-x-1" : ""}`}>←</span>
            {t('admin.common.back', 'Back')}
          </button>
        </div>
      </div>

      <div className="mb-8 flex flex-col xl:flex-row gap-4 xl:items-center bg-gray-50 p-4 rounded-[30px] border border-gray-100">
        <div className="relative flex-1 w-full">
          <i className="fas fa-search absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"></i>
          <input value={q} onChange={e => setQ(e.target.value)} placeholder={t('admin.common.search_placeholder', 'Search...')} className="w-full pl-12 pr-4 py-3 rounded-2xl border-none bg-gray-50 focus:bg-white focus:ring-2 focus:ring-brand-primary/20 transition-all font-medium" />
        </div>

        <div className="flex flex-wrap gap-2 items-center">
          <div className="flex flex-wrap gap-2 bg-white p-2 rounded-2xl shadow-sm border border-gray-100">
            <button onClick={approve} className="bg-green-100 text-green-700 hover:bg-green-600 hover:text-white px-4 py-2 rounded-xl text-xs font-black uppercase transition-all flex items-center gap-2">
              <i className="fas fa-check"></i> {t('admin.reviews_page.approve')}
            </button>
            <button onClick={() => verify(true)} className="bg-blue-100 text-blue-700 hover:bg-blue-600 hover:text-white px-4 py-2 rounded-xl text-xs font-black uppercase transition-all flex items-center gap-2">
              <i className="fas fa-certificate"></i> {t('admin.reviews_page.verify_store')}
            </button>
            <button onClick={() => verify(false)} className="bg-gray-100 text-gray-600 hover:bg-gray-600 hover:text-white px-4 py-2 rounded-xl text-xs font-black uppercase transition-all flex items-center gap-2">
              <i className="fas fa-times"></i> {t('admin.reviews_page.unverify')}
            </button>
            <button onClick={del} className="bg-red-100 text-red-700 hover:bg-red-600 hover:text-white px-4 py-2 rounded-xl text-xs font-black uppercase transition-all flex items-center gap-2">
              <i className="fas fa-trash"></i> {t('admin.common.delete')}
            </button>
          </div>


        </div>
      </div>

      <div className="space-y-4">
        {reviews.map(r => (
          <div key={r.id} className={`group border border-gray-100 p-6 rounded-[24px] hover:shadow-lg hover:border-brand-primary/20 transition-all duration-300 relative ${selected.has(r.id) ? "bg-brand-primary/5 ring-2 ring-brand-primary/20" : "bg-white"}`}>
            <div className="absolute top-4 right-4 z-10">
              <span className="px-2 py-1 rounded-lg text-[10px] font-black uppercase tracking-widest bg-gray-100 text-gray-500">
                #{r.id}
              </span>
            </div>
            <div className="flex flex-col md:flex-row gap-4 items-start">
              <div className="pt-1">
                <input type="checkbox" checked={selected.has(r.id)} onChange={() => toggle(r.id)} className="w-5 h-5 accent-brand-primary rounded-lg cursor-pointer" />
              </div>

              <div className="flex-1">
                <div className="flex flex-wrap items-center gap-3 mb-2">
                  <h3 className="font-bold text-gray-900 text-lg">
                    {isArabic
                      ? (r.name_ar === 'عميل مجهول' ? t('admin.feedback_page.anonymous', 'مجهول') : r.name_ar)
                      : (r.name === 'Anonymous Customer' ? t('admin.feedback_page.anonymous', 'Anonymous') : r.name)
                    }
                    {/* {r.name === 'Anonymous Customer' ? t('admin.feedback_page.anonymous', 'Anonymous') : r.name} */}
                  </h3>
                  {r.contact && <span className="text-[10px] font-bold text-brand-primary bg-brand-primary/10 px-3 py-1 rounded-full uppercase tracking-wider">{r.contact}</span>}
                  <div className="flex items-center text-amber-400 text-sm gap-1 bg-amber-50 px-2 py-1 rounded-lg border border-amber-100">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <i key={i} className={`fas fa-star ${i < r.rating ? "" : "text-gray-300"}`}></i>
                    ))}
                    <span className="text-amber-700 font-bold ml-1">{r.rating}</span>
                  </div>

                  {!r.is_approved && <span className="text-[10px] font-black bg-amber-100 text-amber-700 px-3 py-1 rounded-full uppercase tracking-widest">{t('admin.reviews_page.pending')}</span>}
                  {r.is_verified ? <span className="text-[10px] font-black bg-green-100 text-green-700 px-3 py-1 rounded-full uppercase tracking-widest flex items-center gap-1"><i className="fas fa-check-circle"></i>{t('admin.reviews_page.verified')}</span> : null}
                </div>

                <p className="text-gray-600 leading-relaxed text-wrap mb-4">{r.comment}</p>

                <div className="flex flex-wrap items-center justify-between gap-4 pt-4 border-t border-gray-50 mt-4">
                  <div className="flex items-center gap-4">
                    <span className="text-xs font-bold text-gray-400 bg-gray-50 px-3 py-1 rounded-lg">
                      {t('admin.reviews_page.product_id_label', 'PRODUCT ID:')} <span className="text-gray-600">#{r.product_id}</span>
                    </span>
                    <div className="h-4 w-px bg-gray-200"></div>
                    <button
                      onClick={async () => {
                        try {
                          const res = await axios.post(`${CONFIG.API_URL}/api/reviews/${r.id}/toggle-featured`, {}, { headers: getHeaders() });
                          setReviews(prev => prev.map(item => item.id === r.id ? { ...item, is_featured: res.data.is_featured } : item));
                        } catch (err) { alert("Toggle failed"); }
                      }}
                      className={`text-[10px] font-black uppercase tracking-widest px-3 py-1 rounded-full transition-all ${r.is_featured ? "bg-purple-100 text-purple-700 hover:bg-purple-200" : "bg-gray-100 text-gray-400 hover:bg-gray-200"}`}
                    >
                      {r.is_featured ? `★ ${t('admin.reviews_page.home_featured', 'HOME FEATURED')}` : `☆ ${t('admin.reviews_page.not_featured', 'NOT FEATURED')}`}
                    </button>
                  </div>

                  <button
                    onClick={() => {
                      setEditing(r.id);
                      setEditData({ rating: r.rating, comment: r.comment });
                    }}
                    className="text-xs font-bold text-gray-400 hover:text-brand-primary flex items-center gap-1 group/btn"
                  >
                    <i className="fas fa-pencil-alt group-hover/btn:scale-110 transition-transform"></i>
                    {t('admin.common.edit')}
                  </button>
                </div>
              </div>
            </div>

            {editing === r.id && (
              <div className="mt-6 bg-brand-primary/5 p-6 rounded-[20px] border border-brand-primary/10 animate-fade-in-up">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
                  <div className="md:col-span-2">
                    <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 mb-2 block">Rating</label>
                    <select
                      value={editData.rating}
                      onChange={e => setEditData({ ...editData, rating: e.target.value })}
                      className="w-full border-none p-3 rounded-xl bg-white shadow-sm focus:ring-2 focus:ring-brand-primary/20 font-bold text-brand-primary"
                    >
                      {[5, 4, 3, 2, 1].map(n => <option key={n} value={n}>{n} Stars</option>)}
                    </select>
                  </div>
                  <div className="md:col-span-10">
                    <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 mb-2 block">Comment</label>
                    <textarea
                      className="w-full border-none p-4 rounded-xl bg-white shadow-sm focus:ring-2 focus:ring-brand-primary/20 font-medium h-24 resize-none"
                      value={editData.comment}
                      onChange={e => setEditData({ ...editData, comment: e.target.value })}
                    />
                  </div>
                </div>

                <div className="flex gap-2 justify-end mt-4">
                  <button onClick={() => setEditing(null)} className="px-6 py-2 rounded-xl font-bold text-gray-500 hover:bg-gray-200 transition-all text-xs uppercase tracking-widest">
                    {t('admin.common.cancel')}
                  </button>
                  <button
                    onClick={async () => {
                      try {
                        await axios.put(
                          `${CONFIG.API_URL}/api/admin/reviews/${r.id}`,
                          { id: r.id, ...editData },
                          { headers: getHeaders() }
                        );
                        setEditing(null);
                        fetchReviews();
                      } catch (err) {
                        console.error(err);
                        alert("Update failed");
                      }
                    }}
                    className="bg-brand-primary text-white px-6 py-2 rounded-xl font-black text-xs uppercase tracking-widest hover:scale-105 transition-all shadow-lg shadow-brand-primary/20"
                  >
                    {t('admin.common.save')}
                  </button>
                </div>
              </div>
            )}

          </div>
        ))}
      </div>

      {total > 20 && (
        <div className="flex justify-center gap-4 items-center mt-12">
          <button onClick={() => fetchReviews(Math.max(1, page - 1))} disabled={page <= 1} className="w-10 h-10 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center disabled:opacity-50 transition-all font-bold">{isArabic ? "→" : "←"}</button>
          <div className="text-xs font-black uppercase tracking-widest text-gray-400">{t('admin.common.page', { current: page, total: Math.ceil(total / 20) })}</div>
          <button onClick={() => fetchReviews(page + 1)} disabled={page >= Math.ceil(total / 20)} className="w-10 h-10 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center disabled:opacity-50 transition-all font-bold">{isArabic ? "←" : "→"}</button>
        </div>
      )}
    </div>
  );
});

export default ReviewsAdmin;
